import { limparDecisoes } from './privacidade/retomada-de-decisoes.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Container, Header, SkipLink } from '@meu-mapa-nev/design-system';

import { AlertComFoco } from './AlertComFoco.js';
import { AreaBoundary } from './AreaBoundary.js';
import {
  contaApi,
  type AccessMethod,
  type AccountInitialResult,
  type InitialCorrectionDraft,
  type InitialDraftData,
  type FinalDraftState,
  type FinalResult,
  type AccountContext,
  type LegalReacceptanceDecision,
  type LegalReacceptancePending,
  type ApplicableDocument,
  type Journey,
  type ParticipantDocument,
  type ParticipationChoice,
} from './conta/api.js';
import { useDocumentos } from './conta/useDocumentos.js';
import { DocumentoDaJornada } from './conta/DocumentoDaJornada.js';
import { AvisoDeSessao } from './conta/AvisoDeSessao.js';
import { DesafioAntibot } from './conta/DesafioAntibot.js';
import { TelaDeAutenticacao, type EtapaAutenticacao } from './conta/TelaDeAutenticacao.js';
import type { AcaoGoogleDaConta, PropositoReautenticacao } from './conta/TelaDeConta.js';
import { turnstileAntibot, type Antibot } from './antibot.js';

const TelaDeConta = React.lazy(() =>
  import('./conta/TelaDeConta.js').then((module) => ({ default: module.TelaDeConta })),
);
const TelaDeMinhaJornada = React.lazy(() =>
  import('./conta/TelaDeMinhaJornada.js').then((module) => ({
    default: module.TelaDeMinhaJornada,
  })),
);
const TelaDeParticipacoes = React.lazy(() =>
  import('./conta/TelaDeParticipacoes.js').then((module) => ({
    default: module.TelaDeParticipacoes,
  })),
);
const TelaDeVinculoTurma = React.lazy(() =>
  import('./conta/TelaDeVinculoTurma.js').then((module) => ({
    default: module.TelaDeVinculoTurma,
  })),
);
const FluxoInicialDaConta = React.lazy(() =>
  import('./conta/FluxoInicialDaConta.js').then((module) => ({
    default: module.FluxoInicialDaConta,
  })),
);
const FluxoFinalDaConta = React.lazy(() =>
  import('./conta/FluxoFinalDaConta.js').then((module) => ({
    default: module.FluxoFinalDaConta,
  })),
);
const ResultadoFinalDaConta = React.lazy(() =>
  import('./conta/ResultadoFinalDaConta.js').then((module) => ({
    default: module.ResultadoFinalDaConta,
  })),
);
const ResultadoInicialDaConta = React.lazy(() =>
  import('./resultado/ResultadoInicialDaConta.js').then((module) => ({
    default: module.ResultadoInicialDaConta,
  })),
);
const ReaceiteJuridico = React.lazy(() =>
  import('./privacidade/ReaceiteJuridico.js').then((module) => ({
    default: module.ReaceiteJuridico,
  })),
);

/**
 * Aplicação da participante — autenticação por conta.
 *
 * Substitui o fluxo OTP por turma (`acesso/`) por decisão explícita do
 * responsável pelo projeto em 11/09/2026: a nova experiência de conta
 * (e-mail/senha, Google, Minha Jornada) substitui o fluxo antigo, os dois não
 * convivem. Ver `docs/decisions/2026-09-11-P1-EXPERIENCIA-DA-PARTICIPANTE.md`.
 *
 * A mesma regra estrutural do fluxo anterior continua valendo: quem decide a
 * etapa é o servidor (`AccountContext.next_step`), este componente só desenha
 * o que o servidor mandar.
 */

interface Dependencias {
  readonly api: typeof contaApi;
  readonly antibot: Antibot;
}

export interface AppProps {
  readonly deps?: Partial<Dependencias>;
  /** Token da turma (link de convite). Quando ausente, é lido da URL `?t=`. */
  readonly cohortToken?: string;
}

type Situacao =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'contexto-indisponivel' }
  | { readonly tipo: 'autenticacao'; readonly etapa: EtapaAutenticacao }
  | { readonly tipo: 'vinculo-turma'; readonly documentos: readonly ApplicableDocument[] }
  | { readonly tipo: 'pendencia-sem-token' }
  | { readonly tipo: 'participacoes'; readonly participacoes: readonly ParticipationChoice[] }
  | { readonly tipo: 'sem-participacao' }
  | { readonly tipo: 'reaceite'; readonly pending: LegalReacceptancePending }
  | {
      readonly tipo: 'jornada';
      readonly jornada: Journey;
      readonly initialResult?: AccountInitialResult | undefined;
      readonly documents: readonly ParticipantDocument[];
    }
  | {
      readonly tipo: 'fluxo-inicial';
      readonly participationId: string;
      readonly uiContentVersion: string;
      readonly revision: number;
      readonly expiresAt: string | null;
      readonly expired: boolean;
      readonly correctionDraft?: InitialCorrectionDraft;
      readonly draft?: InitialDraftData;
    }
  | {
      readonly tipo: 'resultado-inicial';
      readonly resultado: AccountInitialResult;
      readonly participationId: string;
      readonly snapshotId?: string | undefined;
    }
  | {
      readonly tipo: 'fluxo-final';
      readonly participationId: string;
      readonly draft: FinalDraftState;
    }
  | {
      readonly tipo: 'resultado-final';
      readonly participationId: string;
      readonly uiContentVersion: string;
      readonly resultado: FinalResult;
      readonly documents: readonly ParticipantDocument[];
    }
  | { readonly tipo: 'conta'; readonly methods: readonly AccessMethod[] }
  | { readonly tipo: 'ajuda' };

type Aviso = { readonly type: 'info' | 'warning' | 'critical'; readonly text: string } | undefined;

function contentVersionFor(situacao: Situacao): string | undefined {
  switch (situacao.tipo) {
    case 'jornada':
      return situacao.jornada.status === 'available'
        ? situacao.jornada.ui_content_version
        : undefined;
    case 'fluxo-inicial':
      return situacao.uiContentVersion;
    case 'fluxo-final':
      return situacao.draft.ui_content_version;
    case 'resultado-inicial':
      return situacao.resultado.uiContentVersion;
    case 'resultado-final':
      return situacao.uiContentVersion;
    default:
      return undefined;
  }
}

function lerParametroDaUrl(nome: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const valor = new URLSearchParams(window.location.search).get(nome);
  return valor !== null && valor.length > 0 ? valor : null;
}

const ACCOUNT_GOOGLE_INTENT_KEY = 'nev:account-google-reauth-intent';

function guardarIntencaoGoogleDaConta(purpose: AcaoGoogleDaConta, expiresAt: string): boolean {
  try {
    window.sessionStorage.setItem(
      ACCOUNT_GOOGLE_INTENT_KEY,
      JSON.stringify({ purpose, expiresAt }),
    );
    return true;
  } catch {
    return false;
  }
}

function consumirIntencaoGoogleDaConta(): AcaoGoogleDaConta | null {
  try {
    const raw = window.sessionStorage.getItem(ACCOUNT_GOOGLE_INTENT_KEY);
    window.sessionStorage.removeItem(ACCOUNT_GOOGLE_INTENT_KEY);
    if (raw === null) return null;
    const intent: unknown = JSON.parse(raw);
    if (typeof intent !== 'object' || intent === null) return null;
    const { purpose, expiresAt } = intent as Record<string, unknown>;
    if (
      (purpose !== 'change-password' && purpose !== 'revoke-sessions') ||
      typeof expiresAt !== 'string' ||
      !Number.isFinite(Date.parse(expiresAt)) ||
      Date.parse(expiresAt) <= Date.now()
    )
      return null;
    return purpose;
  } catch {
    return null;
  }
}

export const App: React.FC<AppProps> = ({ deps, cohortToken }) => {
  const api = deps?.api ?? contaApi;
  const antibot = deps?.antibot ?? turnstileAntibot;

  const informado = cohortToken !== undefined && cohortToken.length > 0 ? cohortToken : null;
  const [token] = useState(() => informado ?? lerParametroDaUrl('t'));

  const [situacao, setSituacao] = useState<Situacao>({ tipo: 'carregando' });
  const [initialResultDate, setInitialResultDate] = useState<string | undefined>();
  const accountReturn = useRef<Situacao | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [documentoEmDownload, setDocumentoEmDownload] = useState<string | null>(null);
  const [aviso, setAviso] = useState<Aviso>(undefined);
  const [expiraEm, setExpiraEm] = useState<Date | null>(null);
  const [loadedContentVersion, setLoadedContentVersion] = useState<string | null>(null);
  const [contentLoadFailed, setContentLoadFailed] = useState(false);
  const [legalSocialToken, setLegalSocialToken] = useState<string | null>(null);
  const [googleAccountProof, setGoogleAccountProof] = useState<{
    purpose: AcaoGoogleDaConta;
    token: string;
  } | null>(null);
  const legalOperation = useRef<{ decisions: string; key: string } | null>(null);
  // Cada intenção de navegação invalida respostas de telas anteriores.
  const navigation = useRef(0);
  const googleAccountOperation = useRef(0);
  const documentParticipation =
    situacao.tipo === 'jornada'
      ? situacao.jornada.participation_id
      : situacao.tipo === 'resultado-final' || situacao.tipo === 'resultado-inicial'
        ? situacao.participationId
        : null;
  const documentState = useDocumentos(
    documentParticipation,
    api,
    situacao.tipo === 'resultado-final' ? 'participant-final' : 'participant-initial',
  );
  const contentVersion = contentVersionFor(situacao);
  const contentReady = contentVersion === undefined || loadedContentVersion === contentVersion;

  useEffect(() => {
    if (contentVersion === undefined) return;
    let cancelled = false;
    setContentLoadFailed(false);
    void import('@meu-mapa-nev/content')
      .then(async ({ getApprovedContentBundle, loadPublishedContentBundle }) => {
        if (getApprovedContentBundle(contentVersion)) return true;
        const bundle = await loadPublishedContentBundle(contentVersion, async (version) => {
          const response = await fetch(`/api/v1/content-bundles/${encodeURIComponent(version)}`, {
            credentials: 'same-origin',
          });
          if (!response.ok) return null;
          return (await response.json()) as {
            version: string;
            content_hash: string;
            canonical_entries: string;
            entries: readonly { key: string; text: string }[];
          };
        });
        return bundle !== undefined;
      })
      .then((ready) => {
        if (cancelled) return;
        if (ready) setLoadedContentVersion(contentVersion);
        else setContentLoadFailed(true);
      })
      .catch(() => {
        if (!cancelled) setContentLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [contentVersion]);

  /** Leva a participante à tela certa a partir do que o servidor decidiu. */
  const roteizarPorContexto = useCallback(
    async (ctx: AccountContext, request = ++navigation.current): Promise<void> => {
      if (request !== navigation.current) return;
      setExpiraEm(new Date(ctx.session_expires_at));

      const precisaVinculo =
        ctx.next_step === 'accept-documents' || ctx.next_step === 'no-participation';

      if (precisaVinculo) {
        if (token === null) {
          setSituacao(
            ctx.next_step === 'accept-documents'
              ? { tipo: 'pendencia-sem-token' }
              : { tipo: 'sem-participacao' },
          );
          return;
        }
        const documentos = await api.cohortDocuments(token);
        if (request !== navigation.current) return;
        if (!documentos.ok) {
          setAviso({ type: 'critical', text: documentos.problem.title });
          setSituacao({ tipo: 'sem-participacao' });
          return;
        }
        setSituacao({ tipo: 'vinculo-turma', documentos: documentos.value.documents });
        return;
      }

      if (ctx.next_step === 'choose-participation') {
        const participacoes = await api.participations();
        if (request !== navigation.current) return;
        if (!participacoes.ok) {
          setAviso({ type: 'critical', text: participacoes.problem.title });
          return;
        }
        if (participacoes.value.participations.length === 1) {
          const selected = await api.selectParticipation(
            participacoes.value.participations[0]!.participation_id,
          );
          if (request !== navigation.current) return;
          if (selected.ok) {
            await roteizarPorContexto(selected.value, request);
            return;
          }
        }
        setSituacao({ tipo: 'participacoes', participacoes: participacoes.value.participations });
        return;
      }

      if (ctx.next_step === 'my-journey') {
        let participationId = ctx.selected_participation_id;
        if (participationId === null) {
          const participacoes = await api.participations();
          if (request !== navigation.current) return;
          if (participacoes.ok && participacoes.value.participations.length === 1) {
            participationId = participacoes.value.participations[0]!.participation_id;
          }
        }
        if (participationId === null) {
          const participacoes = await api.participations();
          if (request !== navigation.current) return;
          setSituacao({
            tipo: 'participacoes',
            participacoes: participacoes.ok ? participacoes.value.participations : [],
          });
          return;
        }
        const jornada = await api.journey(participationId);
        if (request !== navigation.current) return;
        if (!jornada.ok) {
          setAviso({ type: 'critical', text: jornada.problem.title });
          return;
        }
        if (
          jornada.value.status === 'blocked' &&
          jornada.value.reason === 'reacceptance_required'
        ) {
          const pending = await api.legalReacceptance(participationId);
          if (request !== navigation.current) return;
          if (!pending.ok) {
            setAviso({ type: 'critical', text: pending.problem.title });
            return;
          }
          setSituacao({ tipo: 'reaceite', pending: pending.value });
          const social = await api.socialReauthenticationResult();
          if (request !== navigation.current) return;
          if (social.ok) setLegalSocialToken(social.value.reauthentication_token);
          return;
        }
        if (
          jornada.value.status === 'available' &&
          jornada.value.next_step === 'initial-assessment'
        ) {
          const draft = await api.initialDraft(participationId);
          if (request !== navigation.current) return;
          if (!draft.ok) {
            setAviso({ type: 'critical', text: draft.problem.title });
            return;
          }
          setSituacao({
            tipo: 'fluxo-inicial',
            participationId,
            uiContentVersion: draft.value.ui_content_version,
            revision: draft.value.revision,
            expiresAt: draft.value.expires_at,
            expired: draft.value.state === 'expired',
            ...(draft.value.correction_draft === undefined
              ? {}
              : { correctionDraft: draft.value.correction_draft }),
            ...(draft.value.draft === undefined ? {} : { draft: draft.value.draft }),
          });
          return;
        }
        if (jornada.value.status === 'blocked' && jornada.value.reason === 'not_eligible') {
          const result = await api.initialResult(participationId);
          if (request !== navigation.current) return;
          if (!result.ok) {
            setAviso({ type: 'critical', text: result.problem.title });
            return;
          }
          setSituacao({ tipo: 'resultado-inicial', resultado: result.value, participationId });
          return;
        }
        const documentos = await api.documents(participationId);
        if (request !== navigation.current) return;
        const initial =
          jornada.value.status === 'available' && jornada.value.initial_result_id
            ? await api.initialResult(participationId)
            : null;
        if (request !== navigation.current) return;
        if (!documentos.ok) {
          setAviso({ type: 'warning', text: documentos.problem.title });
        }
        setSituacao({
          tipo: 'jornada',
          jornada: jornada.value,
          initialResult: initial?.ok ? initial.value : undefined,
          documents: documentos.ok ? documentos.value.documents : [],
        });
        return;
      }

      if (ctx.next_step === 'reset-password') {
        setSituacao({ tipo: 'autenticacao', etapa: 'recuperar-confirmar' });
        return;
      }

      setSituacao({ tipo: 'sem-participacao' });
    },
    [api, token],
  );

  const carregarContexto = useCallback(
    async (ativo: () => boolean = () => true): Promise<void> => {
      const request = ++navigation.current;
      setGoogleAccountProof(null);
      try {
        const contexto = await api.context();
        if (!ativo() || request !== navigation.current) return;
        if (!contexto.ok) {
          setSituacao(
            contexto.problem.status === 401
              ? { tipo: 'autenticacao', etapa: 'login' }
              : { tipo: 'contexto-indisponivel' },
          );
          return;
        }
        const pendingGoogleAction = consumirIntencaoGoogleDaConta();
        if (pendingGoogleAction !== null) {
          const proof = await api.socialReauthenticationResult();
          if (!ativo() || request !== navigation.current) return;
          const methods = await api.listMethods();
          if (!ativo() || request !== navigation.current) return;
          if (!methods.ok) {
            setAviso({ type: 'critical', text: methods.problem.title });
            await roteizarPorContexto(contexto.value, request);
            return;
          }
          if (
            proof.ok &&
            proof.value.reauthentication_token.startsWith(`${pendingGoogleAction}.`) &&
            Date.parse(proof.value.expires_at) > Date.now()
          ) {
            setGoogleAccountProof({
              purpose: pendingGoogleAction,
              token: proof.value.reauthentication_token,
            });
          } else {
            setAviso({
              type: 'warning',
              text: 'A confirmação com Google não foi concluída. Você pode tentar novamente.',
            });
          }
          accountReturn.current = null;
          setSituacao({ tipo: 'conta', methods: methods.value.methods });
          return;
        }
        await roteizarPorContexto(contexto.value, request);
      } catch {
        if (ativo() && request === navigation.current)
          setSituacao({ tipo: 'contexto-indisponivel' });
      }
    },
    [api, roteizarPorContexto],
  );

  useEffect(() => {
    let ativo = true;
    void carregarContexto(() => ativo);
    return () => {
      ativo = false;
    };
  }, [carregarContexto]);

  const obterTokenAntibot = async (): Promise<string | null> => {
    const tokenAntibot = await antibot.obterToken();
    if (tokenAntibot === null) {
      setAviso({ type: 'critical', text: antibot.motivoIndisponivel() });
    }
    return tokenAntibot;
  };

  const enviarComAntibot = async <T,>(requisicao: () => Promise<T>): Promise<T> => {
    try {
      return await requisicao();
    } finally {
      // O Siteverify consome o token mesmo quando a operação é recusada.
      antibot.renovar();
    }
  };

  const handleLogin = (email: string, password: string): void => {
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const turnstileToken = await obterTokenAntibot();
      if (turnstileToken === null) {
        setEnviando(false);
        return;
      }
      const resultado = await enviarComAntibot(() =>
        api.login({ email, password, turnstileToken }),
      );
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      await roteizarPorContexto(resultado.value);
    })();
  };

  const handleSignup = (email: string, password: string): void => {
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const turnstileToken = await obterTokenAntibot();
      if (turnstileToken === null) {
        setEnviando(false);
        return;
      }
      const resultado = await enviarComAntibot(() =>
        api.signup({
          email,
          password,
          turnstileToken,
          ...(token !== null ? { cohortToken: token } : {}),
        }),
      );
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      setAviso({
        type: 'info',
        text: 'Se este endereço puder ser usado para uma conta nova, você receberá uma confirmação. Entre com seu e-mail e senha para continuar.',
      });
      setSituacao({ tipo: 'autenticacao', etapa: 'login' });
    })();
  };

  const handleGoogle = (): void => {
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const turnstileToken = await obterTokenAntibot();
      if (turnstileToken === null) {
        setEnviando(false);
        return;
      }
      const resultado = await enviarComAntibot(() =>
        api.socialStart({
          turnstileToken,
          ...(token !== null ? { cohortToken: token } : {}),
        }),
      );
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      window.location.assign(resultado.value.authorization_url);
    })();
  };

  const handleRequestRecovery = (email: string): void => {
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const turnstileToken = await obterTokenAntibot();
      if (turnstileToken === null) {
        setEnviando(false);
        return;
      }
      const resultado = await enviarComAntibot(() =>
        api.requestRecovery({ email, turnstileToken }),
      );
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      setAviso({
        type: 'info',
        text: 'Se este endereço tiver uma conta, você receberá um link de recuperação por e-mail.',
      });
    })();
  };

  const handleCompleteRecovery = (password: string): void => {
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const resultado = await api.completeRecovery({ password });
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      setAviso({ type: 'info', text: 'Senha definida. Entre com sua nova senha para continuar.' });
      setSituacao({ tipo: 'autenticacao', etapa: 'login' });
    })();
  };

  const handleAceitarVinculo = (
    decisions: ReadonlyArray<{
      document_code: string;
      version: string;
      content_hash: string;
      decision: 'accepted' | 'refused';
    }>,
  ): void => {
    if (token === null) {
      return;
    }
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const resultado = await api.acceptEnrollment({
        cohortToken: token,
        decisions,
        idempotencyKey: crypto.randomUUID(),
      });
      if (!resultado.ok) {
        setEnviando(false);
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      const contexto = await api.context();
      setEnviando(false);
      if (!contexto.ok) {
        setAviso({ type: 'critical', text: contexto.problem.title });
        return;
      }
      await roteizarPorContexto(contexto.value);
    })();
  };

  const handleSelecionarParticipacao = (participationId: string): void => {
    const request = ++navigation.current;
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const resultado = await api.selectParticipation(participationId);
      if (request !== navigation.current) return;
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      await roteizarPorContexto(resultado.value, request);
    })();
  };

  const abrirConta = (): void => {
    const request = ++navigation.current;
    setGoogleAccountProof(null);
    if (situacao.tipo !== 'conta') accountReturn.current = situacao;
    void (async () => {
      const metodos = await api.listMethods();
      if (request !== navigation.current) return;
      setSituacao({ tipo: 'conta', methods: metodos.ok ? metodos.value.methods : [] });
    })();
  };

  const handleReautenticar = async (
    password: string,
    purpose: PropositoReautenticacao,
  ): Promise<{ token: string } | null> => {
    setEnviando(true);
    setAviso(undefined);
    const turnstileToken = await obterTokenAntibot();
    if (turnstileToken === null) {
      setEnviando(false);
      return null;
    }
    const resultado = await enviarComAntibot(() =>
      api.reauthenticateWithPassword({
        password,
        purpose,
        turnstileToken,
      }),
    );
    setEnviando(false);
    if (!resultado.ok) {
      setAviso({ type: 'critical', text: resultado.problem.title });
      return null;
    }
    return { token: resultado.value.reauthentication_token };
  };

  const iniciarConfirmacaoGoogleDaConta = (purpose: AcaoGoogleDaConta): void => {
    const navigationRequest = navigation.current;
    const operation = ++googleAccountOperation.current;
    setGoogleAccountProof(null);
    try {
      window.sessionStorage.removeItem(ACCOUNT_GOOGLE_INTENT_KEY);
    } catch {
      /* storage indisponível */
    }
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const result = await api.reauthenticateWithGoogle({ purpose });
      if (navigationRequest !== navigation.current || operation !== googleAccountOperation.current)
        return;
      setEnviando(false);
      if (!result.ok) {
        setAviso({ type: 'critical', text: result.problem.title });
        return;
      }
      if (!guardarIntencaoGoogleDaConta(purpose, result.value.expires_at)) {
        setAviso({
          type: 'critical',
          text: 'Não foi possível guardar esta confirmação. Tente novamente neste navegador.',
        });
        return;
      }
      window.location.assign(result.value.authorization_url);
    })();
  };

  const iniciarReaceiteGoogle = (): void => {
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      const result = await api.reauthenticateWithGoogle({ purpose: 'legal-reaccept' });
      setEnviando(false);
      if (!result.ok) {
        setAviso({ type: 'critical', text: result.problem.title });
        return;
      }
      window.location.assign(result.value.authorization_url);
    })();
  };

  const registrarReaceite = (
    decisions: readonly LegalReacceptanceDecision[],
    password: string,
  ): void => {
    if (situacao.tipo !== 'reaceite') return;
    const participationId = situacao.pending.participation_id;
    const signature = JSON.stringify(decisions);
    if (legalOperation.current?.decisions !== signature)
      legalOperation.current = { decisions: signature, key: crypto.randomUUID() };
    const key = legalOperation.current.key;
    setEnviando(true);
    setAviso(undefined);
    void (async () => {
      let proof = legalSocialToken;
      if (proof === null) {
        const turnstileToken = await obterTokenAntibot();
        if (turnstileToken === null) {
          setEnviando(false);
          return;
        }
        const auth = await enviarComAntibot(() =>
          api.reauthenticateWithPassword({
            password,
            purpose: 'legal-reaccept',
            turnstileToken,
          }),
        );
        if (!auth.ok) {
          setEnviando(false);
          setAviso({ type: 'critical', text: auth.problem.title });
          return;
        }
        proof = auth.value.reauthentication_token;
      }
      const result = await api.recordLegalReacceptance({
        participationId,
        decisions,
        idempotencyKey: key,
        reauthToken: proof,
      });
      setEnviando(false);
      setLegalSocialToken(null);
      if (!result.ok) {
        setAviso({ type: 'critical', text: result.problem.title });
        return;
      }
      limparDecisoes(participationId);
      if (result.value.legal_acceptance_state === 'refused') {
        setAviso({
          type: 'warning',
          text: 'Sua recusa foi registrada. Esta participação não pode continuar na Jornada.',
        });
      }
      const context = await api.context();
      if (context.ok) await roteizarPorContexto(context.value);
      else setAviso({ type: 'critical', text: context.problem.title });
    })();
  };

  const handleChangePassword = (newPassword: string, reauthToken: string): void => {
    setGoogleAccountProof(null);
    setEnviando(true);
    void (async () => {
      const resultado = await api.changePassword({ newPassword, reauthToken });
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      ++navigation.current;
      accountReturn.current = null;
      setExpiraEm(null);
      setAviso({ type: 'info', text: 'Senha alterada. Entre novamente para continuar.' });
      setSituacao({ tipo: 'autenticacao', etapa: 'login' });
    })();
  };

  const handleLinkGoogle = (reauthToken: string): void => {
    setEnviando(true);
    void (async () => {
      const resultado = await api.methodsLinkStart({
        reauthToken,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      window.location.assign(resultado.value.authorization_url);
    })();
  };

  const handleUnlinkGoogle = (identityId: string, reauthToken: string): void => {
    setEnviando(true);
    void (async () => {
      const resultado = await api.unlinkMethod({
        identityId,
        reauthToken,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      setAviso({ type: 'info', text: 'Google desvinculado.' });
      abrirConta();
    })();
  };

  const handleRevokeSessions = (reauthToken: string): void => {
    setGoogleAccountProof(null);
    setEnviando(true);
    void (async () => {
      const resultado = await api.revokeAllSessions({
        reauthToken,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);
      if (!resultado.ok) {
        setAviso({ type: 'critical', text: resultado.problem.title });
        return;
      }
      ++navigation.current;
      accountReturn.current = null;
      setExpiraEm(null);
      setAviso({ type: 'info', text: 'Sessões encerradas. Entre novamente para continuar.' });
      setSituacao({ tipo: 'autenticacao', etapa: 'login' });
    })();
  };

  const encerrarSessao = (): void => {
    const request = ++navigation.current;
    setGoogleAccountProof(null);
    try {
      window.sessionStorage.removeItem(ACCOUNT_GOOGLE_INTENT_KEY);
    } catch {
      /* storage indisponível */
    }
    void (async () => {
      await api.logout();
      if (request !== navigation.current) return;
      setExpiraEm(null);
      accountReturn.current = null;
      setAviso(undefined);
      setSituacao({ tipo: 'autenticacao', etapa: 'login' });
    })();
  };

  const autenticada =
    situacao.tipo !== 'carregando' &&
    situacao.tipo !== 'contexto-indisponivel' &&
    situacao.tipo !== 'autenticacao';

  const abrirResumoJornada = (participationId: string): void => {
    const request = ++navigation.current;
    void (async () => {
      const jornada = await api.journey(participationId);
      if (request !== navigation.current) return;
      if (!jornada.ok) {
        setAviso({ type: 'critical', text: jornada.problem.title });
        return;
      }
      const documents = await api.documents(participationId);
      if (request !== navigation.current) return;
      const initial =
        jornada.value.status === 'available' && jornada.value.initial_result_id
          ? await api.initialResult(participationId)
          : null;
      if (request !== navigation.current) return;
      setSituacao({
        tipo: 'jornada',
        jornada: jornada.value,
        initialResult: initial?.ok ? initial.value : undefined,
        documents: documents.ok ? documents.value.documents : [],
      });
    })();
  };

  const abrirResultadoFinal = (participationId: string, uiContentVersion: string): void => {
    const request = ++navigation.current;
    void (async () => {
      const initial = await api.initialResult(participationId);
      if (request !== navigation.current) return;
      setInitialResultDate(initial.ok ? initial.value.generatedAt : undefined);
      const result = await api.finalResult(participationId);
      if (request !== navigation.current) return;
      if (!result.ok) {
        setAviso({ type: 'critical', text: result.problem.title });
        return;
      }
      const documents = await api.documents(participationId);
      if (request !== navigation.current) return;
      setSituacao({
        tipo: 'resultado-final',
        participationId,
        uiContentVersion,
        resultado: result.value,
        documents: documents.ok ? documents.value.documents : [],
      });
    })();
  };

  const baixarDocumento = (documentId: string): void => {
    setDocumentoEmDownload(documentId);
    setAviso(undefined);
    void (async () => {
      const result = await api.downloadDocument(documentId, crypto.randomUUID());
      setDocumentoEmDownload(null);
      if (!result.ok) {
        setAviso({ type: 'critical', text: result.problem.title });
        return;
      }
      const url = URL.createObjectURL(result.value.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.value.filename;
      link.click();
      URL.revokeObjectURL(url);
    })();
  };

  const voltarAoContexto = (): void => {
    const request = ++navigation.current;
    setGoogleAccountProof(null);
    const previous = accountReturn.current;
    accountReturn.current = null;
    if (previous?.tipo === 'fluxo-inicial') {
      void carregarContexto();
    } else if (previous?.tipo === 'fluxo-final') {
      void (async () => {
        const draft = await api.finalDraft(previous.participationId);
        if (request !== navigation.current) return;
        if (draft.ok) setSituacao({ ...previous, draft: draft.value });
        else setAviso({ type: 'critical', text: draft.problem.title });
      })();
    } else if (previous && !['conta', 'ajuda', 'autenticacao'].includes(previous.tipo)) {
      setSituacao(previous);
    } else {
      void carregarContexto();
    }
  };

  return (
    <>
      <SkipLink targetId="main-content" />
      <Header title="Meu Mapa NEV" subtitle="Jornada NEV de Emagrecimento">
        {autenticada && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void carregarContexto();
              }}
            >
              Jornada
            </Button>
            <Button type="button" variant="outline" onClick={abrirConta}>
              Minha conta
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                ++navigation.current;
                accountReturn.current = situacao;
                setSituacao({ tipo: 'ajuda' });
              }}
            >
              Ajuda
            </Button>
          </>
        )}
      </Header>

      <main id="main-content" tabIndex={-1} style={{ padding: 'var(--nev-space-6) 0' }}>
        <Container size="reading">
          <AreaBoundary key={situacao.tipo}>
            <React.Suspense
              fallback={
                <p role="status" aria-live="polite">
                  Carregando área…
                </p>
              }
            >
              {expiraEm !== null && expiraEm.getTime() - Date.now() < 5 * 60_000 && (
                <AvisoDeSessao
                  expiresAt={expiraEm}
                  now={new Date()}
                  onExtend={() => {
                    void api.context();
                  }}
                  onEnd={encerrarSessao}
                />
              )}

              {aviso !== undefined &&
                situacao.tipo !== 'autenticacao' &&
                situacao.tipo !== 'conta' && (
                  <AlertComFoco type={aviso.type} title="Aviso">
                    {aviso.text}
                  </AlertComFoco>
                )}

              {situacao.tipo === 'carregando' && aviso === undefined && (
                <p role="status" aria-live="polite">
                  Carregando…
                </p>
              )}
              {situacao.tipo === 'carregando' && aviso !== undefined && (
                <Button
                  type="button"
                  onClick={() => {
                    setAviso(undefined);
                    void carregarContexto();
                  }}
                >
                  Tentar novamente
                </Button>
              )}

              {situacao.tipo === 'contexto-indisponivel' && (
                <section>
                  <AlertComFoco type="critical" title="Não foi possível verificar sua sessão">
                    Verifique a conexão e tente novamente. Seus dados não foram alterados.
                  </AlertComFoco>
                  <Button
                    type="button"
                    onClick={() => {
                      setAviso(undefined);
                      setSituacao({ tipo: 'carregando' });
                      void carregarContexto();
                    }}
                  >
                    Tentar novamente
                  </Button>
                </section>
              )}

              {!contentReady &&
                contentVersion !== undefined &&
                (contentLoadFailed ? (
                  <Alert type="critical" title="Conteúdo indisponível">
                    A versão aprovada desta participação não está disponível. Tente novamente mais
                    tarde.
                  </Alert>
                ) : (
                  <p role="status" aria-live="polite">
                    Carregando conteúdo aprovado…
                  </p>
                ))}

              {situacao.tipo === 'autenticacao' && (
                <TelaDeAutenticacao
                  etapa={situacao.etapa}
                  onLogin={handleLogin}
                  onSignup={handleSignup}
                  onGoogle={handleGoogle}
                  onRequestRecovery={handleRequestRecovery}
                  onCompleteRecovery={handleCompleteRecovery}
                  onNavigate={(etapa) => {
                    setAviso(undefined);
                    setSituacao({ tipo: 'autenticacao', etapa });
                  }}
                  submitting={enviando}
                  aviso={aviso}
                  desafioAntibot={
                    situacao.etapa !== 'recuperar-confirmar' ? (
                      <DesafioAntibot antibot={antibot} />
                    ) : undefined
                  }
                />
              )}

              {situacao.tipo === 'vinculo-turma' && (
                <TelaDeVinculoTurma
                  documents={situacao.documentos}
                  onSubmit={handleAceitarVinculo}
                  submitting={enviando}
                />
              )}

              {situacao.tipo === 'pendencia-sem-token' && (
                <Alert type="warning" title="Confirmação pendente">
                  Sua conta tem uma participação aguardando a confirmação dos documentos. Use o link
                  da turma que você recebeu para concluir, ou procure o atendimento.
                </Alert>
              )}

              {situacao.tipo === 'participacoes' && (
                <TelaDeParticipacoes
                  participations={situacao.participacoes}
                  onSelect={handleSelecionarParticipacao}
                  submitting={enviando}
                />
              )}

              {situacao.tipo === 'sem-participacao' && (
                <Alert type="info" title="Nenhuma participação encontrada">
                  Sua conta ainda não está vinculada a nenhuma turma. Use o link que você recebeu
                  para entrar em uma.
                </Alert>
              )}

              {situacao.tipo === 'reaceite' && (
                <ReaceiteJuridico
                  key={`${situacao.pending.participation_id}:${situacao.pending.legal_bundle_version}`}
                  pending={situacao.pending}
                  onSubmit={registrarReaceite}
                  onStartGoogle={iniciarReaceiteGoogle}
                  onSwitchParticipation={() => {
                    const request = ++navigation.current;
                    void (async () => {
                      const participacoes = await api.participations();
                      if (request !== navigation.current) return;
                      setSituacao({
                        tipo: 'participacoes',
                        participacoes: participacoes.ok ? participacoes.value.participations : [],
                      });
                    })();
                  }}
                  socialProofReady={legalSocialToken !== null}
                  submitting={enviando}
                  antibot={
                    legalSocialToken === null ? <DesafioAntibot antibot={antibot} /> : undefined
                  }
                />
              )}

              {contentReady && situacao.tipo === 'jornada' && (
                <TelaDeMinhaJornada
                  onDocumentRequested={documentState.afterRequest}
                  api={api}
                  jornada={situacao.jornada}
                  initialResult={situacao.initialResult}
                  documents={documentState.documents}
                  downloadingDocumentId={documentoEmDownload}
                  onDownloadDocument={baixarDocumento}
                  queryFailed={documentState.queryFailed}
                  onRecheckDocuments={documentState.recheck}
                  onOpenInitial={() => {
                    setAviso(undefined);
                    if (situacao.jornada.status !== 'available') return;
                    const request = ++navigation.current;
                    const journey = situacao.jornada;
                    void (async () => {
                      const result = await api.initialResult(journey.participation_id);
                      if (request !== navigation.current) return;
                      if (result.ok)
                        setSituacao({
                          tipo: 'resultado-inicial',
                          resultado: result.value,
                          participationId: journey.participation_id,
                          snapshotId: journey.initial_result_id ?? undefined,
                        });
                      else setAviso({ type: 'critical', text: result.problem.title });
                    })();
                  }}
                  onOpenFinal={() => {
                    setAviso(undefined);
                    if (situacao.jornada.status !== 'available') return;
                    if (situacao.jornada.next_step === 'final-assessment') {
                      const request = ++navigation.current;
                      void (async () => {
                        const draft = await api.finalDraft(situacao.jornada.participation_id);
                        if (request !== navigation.current) return;
                        if (!draft.ok) {
                          setAviso({ type: 'critical', text: draft.problem.title });
                          return;
                        }
                        setSituacao({
                          tipo: 'fluxo-final',
                          participationId: situacao.jornada.participation_id,
                          draft: draft.value,
                        });
                      })();
                    } else {
                      abrirResultadoFinal(
                        situacao.jornada.participation_id,
                        situacao.jornada.ui_content_version,
                      );
                    }
                  }}
                  onTrocarParticipacao={() => {
                    const request = ++navigation.current;
                    void (async () => {
                      const participacoes = await api.participations();
                      if (request !== navigation.current) return;
                      setSituacao({
                        tipo: 'participacoes',
                        participacoes: participacoes.ok ? participacoes.value.participations : [],
                      });
                    })();
                  }}
                />
              )}

              {contentReady && situacao.tipo === 'fluxo-inicial' && (
                <FluxoInicialDaConta
                  api={api}
                  participationId={situacao.participationId}
                  uiContentVersion={situacao.uiContentVersion}
                  initialRevision={situacao.revision}
                  expiresAt={situacao.expiresAt}
                  expired={situacao.expired}
                  correctionDraft={situacao.correctionDraft}
                  draft={situacao.draft}
                  onResult={(resultado) => {
                    ++navigation.current;
                    if (resultado.eligible) abrirResumoJornada(situacao.participationId);
                    else
                      setSituacao({
                        tipo: 'resultado-inicial',
                        resultado,
                        participationId: situacao.participationId,
                      });
                  }}
                  onCorrectionReceipt={() => {
                    setAviso({ type: 'info', text: 'Correção inicial registrada.' });
                    void abrirResumoJornada(situacao.participationId);
                  }}
                  onExit={() => abrirResumoJornada(situacao.participationId)}
                />
              )}

              {contentReady && situacao.tipo === 'fluxo-final' && (
                <FluxoFinalDaConta
                  api={api}
                  participationId={situacao.participationId}
                  draft={situacao.draft}
                  onResult={(resultado) => {
                    const request = ++navigation.current;
                    void (async () => {
                      const initial = await api.initialResult(situacao.participationId);
                      if (request !== navigation.current) return;
                      setInitialResultDate(initial.ok ? initial.value.generatedAt : undefined);
                      const listed = await api.documents(situacao.participationId);
                      if (request !== navigation.current) return;
                      setSituacao({
                        tipo: 'resultado-final',
                        participationId: situacao.participationId,
                        uiContentVersion: situacao.draft.ui_content_version,
                        resultado,
                        documents: listed.ok ? listed.value.documents : [],
                      });
                    })();
                  }}
                  onExit={() => abrirResumoJornada(situacao.participationId)}
                />
              )}

              {contentReady && situacao.tipo === 'resultado-final' && (
                <ResultadoFinalDaConta
                  onDocumentRequested={documentState.afterRequest}
                  api={api}
                  participationId={situacao.participationId}
                  result={situacao.resultado}
                  uiContentVersion={situacao.uiContentVersion}
                  documents={documentState.documents}
                  downloadingDocumentId={documentoEmDownload}
                  onDownloadDocument={baixarDocumento}
                  onRefreshDocuments={documentState.recheck}
                  queryFailed={documentState.queryFailed}
                  initialDate={initialResultDate}
                  onExit={() => abrirResumoJornada(situacao.participationId)}
                />
              )}

              {contentReady && situacao.tipo === 'resultado-inicial' && (
                <>
                  {situacao.resultado.eligible && (
                    <DocumentoDaJornada
                      onRequested={documentState.afterRequest}
                      api={api}
                      participationId={situacao.participationId}
                      documents={documentState.documents}
                      kind="participant-initial"
                      onDownload={baixarDocumento}
                      downloadingId={documentoEmDownload}
                      queryFailed={documentState.queryFailed}
                      onRecheck={documentState.recheck}
                    />
                  )}
                  <ResultadoInicialDaConta
                    resultado={situacao.resultado}
                    chartPath={
                      situacao.snapshotId
                        ? `/api/v1/result-renders/${situacao.snapshotId}`
                        : undefined
                    }
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => abrirResumoJornada(situacao.participationId)}
                  >
                    Voltar à minha jornada
                  </Button>
                </>
              )}

              {situacao.tipo === 'ajuda' && (
                <section>
                  <h1>Ajuda com sua jornada</h1>
                  <p>
                    Seus materiais estão na Hotmart. Para localizar sua turma ou pedir atendimento,
                    entre em contato com a equipe pelo e-mail informado nos documentos de
                    participação.
                  </p>
                  <p>
                    <a href="mailto:victoriaganzaroli@gmail.com">Enviar e-mail para a equipe</a>
                  </p>
                  <Button type="button" variant="outline" onClick={voltarAoContexto}>
                    Voltar à jornada
                  </Button>
                </section>
              )}
              {situacao.tipo === 'conta' && (
                <>
                  <Button type="button" variant="outline" onClick={voltarAoContexto}>
                    Voltar à minha jornada
                  </Button>
                  <TelaDeConta
                    methods={situacao.methods}
                    onReauthenticate={handleReautenticar}
                    onReauthenticateWithGoogle={iniciarConfirmacaoGoogleDaConta}
                    googleProof={googleAccountProof}
                    onDiscardGoogleProof={() => {
                      ++googleAccountOperation.current;
                      setEnviando(false);
                      setGoogleAccountProof(null);
                    }}
                    onChangePassword={handleChangePassword}
                    onLinkGoogle={handleLinkGoogle}
                    onUnlinkGoogle={handleUnlinkGoogle}
                    onRevokeSessions={handleRevokeSessions}
                    onLogout={encerrarSessao}
                    submitting={enviando}
                    aviso={aviso}
                    desafioAntibot={<DesafioAntibot antibot={antibot} />}
                  />
                </>
              )}
            </React.Suspense>
          </AreaBoundary>
        </Container>
      </main>
    </>
  );
};

export default App;
